import { useState, useEffect } from 'react';
import { entityTypeService } from '@/services/kbService';

export interface EntityTypeOption {
  value: string;
  label: string;
}

// Fallback options used while loading from API
const FALLBACK_ENTITY_TYPE_OPTIONS: EntityTypeOption[] = [
  { value: 'PERSON', label: 'Person' },
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'LOCATION', label: 'Location' },
  { value: 'DATE', label: 'Date' },
  { value: 'SAFETY', label: 'Safety' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'COMPLIANCE', label: 'Compliance' },
  { value: 'PRODUCT', label: 'Product' },
  { value: 'MEASUREMENT', label: 'Measurement' },
  { value: 'MONEY', label: 'Money' },
  { value: 'MATERIAL', label: 'Material' },
  { value: 'PLANT', label: 'Plant' },
  { value: 'SITE', label: 'Site' },
  { value: 'OTHER', label: 'Other' },
];

// Module-level cache so all components share the same data
let cachedOptions: EntityTypeOption[] | null = null;
let fetchPromise: Promise<EntityTypeOption[]> | null = null;

async function fetchEntityTypes(): Promise<EntityTypeOption[]> {
  try {
    const types = await entityTypeService.getEntityTypes({
      limit: 200,
      active_only: true,
      include_system: true,
    });

    const options = types
      .map((t) => ({
        value: t.name || t.code?.toUpperCase() || '',
        label: t.label || t.name || t.code || '',
      }))
      .filter((o) => o.value)
      .sort((a, b) => a.label.localeCompare(b.label));

    return options.length > 0 ? options : FALLBACK_ENTITY_TYPE_OPTIONS;
  } catch {
    return FALLBACK_ENTITY_TYPE_OPTIONS;
  }
}

/**
 * Hook that fetches entity type options from the backend API.
 * Uses module-level caching so the API is only called once across all components.
 * Falls back to a static list if the API is unavailable.
 */
export function useEntityTypeOptions(): {
  entityTypeOptions: EntityTypeOption[];
  loading: boolean;
  refresh: () => void;
} {
  const [options, setOptions] = useState<EntityTypeOption[]>(
    cachedOptions || FALLBACK_ENTITY_TYPE_OPTIONS
  );
  const [loading, setLoading] = useState(!cachedOptions);

  useEffect(() => {
    if (cachedOptions) {
      setOptions(cachedOptions);
      setLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = fetchEntityTypes();
    }

    fetchPromise.then((result) => {
      cachedOptions = result;
      setOptions(result);
      setLoading(false);
    });
  }, []);

  const refresh = () => {
    setLoading(true);
    cachedOptions = null;
    fetchPromise = fetchEntityTypes();
    fetchPromise.then((result) => {
      cachedOptions = result;
      setOptions(result);
      setLoading(false);
    });
  };

  return { entityTypeOptions: options, loading, refresh };
}
